import { getPartyMemberDisplayName } from "./party-chat";
import {
  getAnnouncedCategoriesInOrder,
  parseTieBreakerMode,
  rankEntriesWithTieBreakerMode,
  type TieBreakerCategoryInput,
  type TieBreakerMode,
} from "./tie-breakers";
import type {
  PodiumPass,
  PodiumPassStyle,
  PodiumPassVariant,
  PodiumPlace,
} from "./types/parties-api";

export const PODIUM_PLACE_LIMIT = 3;

const PASS_MOVE_VARIANTS = [
  "claim",
  "takes",
  "movesPast",
  "jumps",
  "knocks",
] as const satisfies readonly PodiumPassVariant[];

const PASS_ENTRY_VARIANTS = [
  "stepsOn",
  "claim",
  "jumps",
  "takes",
  "movesPast",
] as const satisfies readonly PodiumPassVariant[];

const CATCH_MOVE_VARIANTS = [
  "catches",
  "pullsEven",
  "joins",
] as const satisfies readonly PodiumPassVariant[];

const CATCH_ENTRY_VARIANTS = [
  "beside",
  "joins",
  "catches",
  "pullsEven",
] as const satisfies readonly PodiumPassVariant[];

export interface PodiumParticipant {
  userId: string;
  name: string;
}

export interface PodiumRankingInput {
  mode: TieBreakerMode;
  participants: readonly PodiumParticipant[];
  categories: readonly TieBreakerCategoryInput[];
  votesByUserId: Record<string, Record<string, string>>;
}

export interface PodiumPartySource {
  id: string;
  awardShowId: string;
  tieBreakerMode: string;
}

export interface PodiumParticipantSource {
  partyId: string;
  userId: string;
  username?: string | null;
  name?: string | null;
  email?: string | null;
}

export interface PodiumVoteSource {
  partyId: string;
  userId: string;
  categoryId: string;
  nomineeId: string;
}

export interface PodiumCategorySource {
  id: string;
  awardShowId: string;
  orderIndex: number;
}

export interface PodiumResultSource {
  categoryId: string;
  nomineeId: string;
  announcedAt: Date | string;
}

interface Standing {
  rank: number;
  correctCount: number;
}

function isPodiumPlace(rank: number): rank is PodiumPlace {
  return rank === 1 || rank === 2 || rank === 3;
}

export function podiumPassVariantsFor(input: {
  style: PodiumPassStyle;
  fromPlace: number;
}): readonly PodiumPassVariant[] {
  const entering = input.fromPlace > PODIUM_PLACE_LIMIT;
  if (input.style === "catch") {
    return entering ? CATCH_ENTRY_VARIANTS : CATCH_MOVE_VARIANTS;
  }
  return entering ? PASS_ENTRY_VARIANTS : PASS_MOVE_VARIANTS;
}

export function pickPodiumPassVariant(
  seed: string,
  variants: readonly PodiumPassVariant[],
): PodiumPassVariant {
  if (variants.length === 0) {
    return "claim";
  }
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }
  return variants[hash % variants.length] ?? "claim";
}

function announcedAtIso(value: Date | string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  const time = date.getTime();
  if (!Number.isFinite(time)) {
    return null;
  }
  return date.toISOString();
}

function countCorrect(
  userId: string,
  categories: readonly TieBreakerCategoryInput[],
  votesByUserId: Record<string, Record<string, string>>,
): number {
  const votes = votesByUserId[userId] ?? {};
  let correct = 0;
  for (const category of categories) {
    const pick = votes[category.id];
    if (pick && category.resultNomineeIds.includes(pick)) {
      correct += 1;
    }
  }
  return correct;
}

function standingsFor(
  input: PodiumRankingInput,
  categories: readonly TieBreakerCategoryInput[],
): Map<string, Standing> {
  const entries = input.participants.map((participant) => ({
    userId: participant.userId,
    correctCount: countCorrect(
      participant.userId,
      categories,
      input.votesByUserId,
    ),
  }));
  const ranked = rankEntriesWithTieBreakerMode(entries, {
    mode: input.mode,
    categories,
    votesByUserId: input.votesByUserId,
    participantUserIds: input.participants.map((participant) => participant.userId),
  });
  return new Map(
    ranked.map((entry) => [
      entry.userId,
      { rank: entry.rank, correctCount: entry.correctCount },
    ]),
  );
}

function chooseRival(
  candidates: readonly { userId: string }[],
  previous: Map<string, Standing>,
  next: Map<string, Standing>,
): string | null {
  if (candidates.length === 0) {
    return null;
  }
  const sorted = [...candidates].sort((left, right) => {
    const leftBefore = previous.get(left.userId)?.rank ?? Number.POSITIVE_INFINITY;
    const rightBefore = previous.get(right.userId)?.rank ?? Number.POSITIVE_INFINITY;
    if (leftBefore !== rightBefore) {
      return leftBefore - rightBefore;
    }
    const leftAfter = next.get(left.userId)?.rank ?? 0;
    const rightAfter = next.get(right.userId)?.rank ?? 0;
    if (leftAfter !== rightAfter) {
      return rightAfter - leftAfter;
    }
    return left.userId.localeCompare(right.userId);
  });
  return sorted[0]?.userId ?? null;
}

function passForClimber(input: {
  climberUserId: string;
  categoryId: string;
  announcedAt: string;
  names: Map<string, string>;
  previous: Map<string, Standing>;
  next: Map<string, Standing>;
  participants: readonly PodiumParticipant[];
}): PodiumPass | null {
  const before = input.previous.get(input.climberUserId);
  const after = input.next.get(input.climberUserId);
  if (
    !before ||
    !after ||
    !isPodiumPlace(after.rank) ||
    after.rank >= before.rank
  ) {
    return null;
  }

  const aheadBefore = input.participants.filter((participant) => {
    if (participant.userId === input.climberUserId) {
      return false;
    }
    const standing = input.previous.get(participant.userId);
    return standing !== undefined && standing.rank < before.rank;
  });
  const overtaken = aheadBefore.filter((participant) => {
    const standing = input.next.get(participant.userId);
    return standing !== undefined && standing.rank > after.rank;
  });
  const caught = aheadBefore.filter((participant) => {
    const standing = input.next.get(participant.userId);
    return standing !== undefined && standing.rank === after.rank;
  });

  let style: PodiumPassStyle | null = null;
  let rivalId: string | null = null;

  if (overtaken.length > 0 && caught.length === 0) {
    style = "pass";
    rivalId = chooseRival(overtaken, input.previous, input.next);
  } else if (caught.length > 0) {
    style = "catch";
    rivalId = chooseRival(caught, input.previous, input.next);
  }

  if (!style || !rivalId) {
    return null;
  }

  const variants = podiumPassVariantsFor({
    style,
    fromPlace: before.rank,
  });
  return {
    categoryId: input.categoryId,
    announcedAt: input.announcedAt,
    climberUserId: input.climberUserId,
    climberName: input.names.get(input.climberUserId) ?? "Member",
    passedUserId: rivalId,
    passedName: input.names.get(rivalId) ?? "Member",
    fromPlace: before.rank,
    toPlace: after.rank,
    style,
    variant: pickPodiumPassVariant(
      `${input.categoryId}:${input.climberUserId}:${after.rank}:${style}`,
      variants,
    ),
  };
}

const OTHER_CLIMBER_NAME_LIMIT = 2;

function comparePodiumPasses(left: PodiumPass, right: PodiumPass): number {
  if (left.toPlace !== right.toPlace) {
    return left.toPlace - right.toPlace;
  }
  if (left.fromPlace !== right.fromPlace) {
    return left.fromPlace - right.fromPlace;
  }
  return left.climberUserId.localeCompare(right.climberUserId);
}

/**
 * Collapse everyone who moved into the same place on one call into a
 * single item led by the first climber, so a call yields at most one
 * item per podium place.
 */
function summarizePlace(
  climbs: readonly PodiumPass[],
  next: Map<string, Standing>,
): PodiumPass | null {
  const [lead, ...others] = climbs;
  if (!lead) {
    return null;
  }
  if (others.length === 0) {
    return lead;
  }
  const climberIds = new Set(climbs.map((climb) => climb.climberUserId));
  const joinedExistingTie = [...next.entries()].some(
    ([userId, standing]) =>
      standing.rank === lead.toPlace && !climberIds.has(userId),
  );
  const style: PodiumPassStyle = joinedExistingTie ? "catch" : "pass";
  return {
    ...lead,
    style,
    variant: pickPodiumPassVariant(
      `${lead.categoryId}:${lead.climberUserId}:${lead.toPlace}:${style}`,
      podiumPassVariantsFor({ style, fromPlace: lead.fromPlace }),
    ),
    otherClimbers: {
      names: others
        .slice(0, OTHER_CLIMBER_NAME_LIMIT)
        .map((climb) => climb.climberName),
      count: others.length,
    },
  };
}

/**
 * Replay announced categories and emit a chat moment whenever someone
 * moves into or up the podium (1st, 2nd, 3rd) by passing or catching
 * a person who was ahead. Keeping a place while a tied rival drops is
 * not a move. Each call yields at most one item per podium place.
 */
export function detectPodiumPasses(input: PodiumRankingInput): PodiumPass[] {
  if (input.participants.length < 2) {
    return [];
  }

  const announced = getAnnouncedCategoriesInOrder(input.categories);
  const names = new Map(
    input.participants.map((participant) => [participant.userId, participant.name]),
  );
  let previous = standingsFor(input, []);
  const passes: PodiumPass[] = [];

  for (let index = 0; index < announced.length; index += 1) {
    const category = announced[index];
    if (!category) {
      continue;
    }
    const announcedAt = announcedAtIso(category.announcedAt);
    const soFar = announced.slice(0, index + 1);
    const next = standingsFor(input, soFar);

    if (announcedAt) {
      const climbsByPlace = new Map<PodiumPlace, PodiumPass[]>();
      for (const participant of input.participants) {
        const pass = passForClimber({
          climberUserId: participant.userId,
          categoryId: category.id,
          announcedAt,
          names,
          previous,
          next,
          participants: input.participants,
        });
        if (pass) {
          const climbs = climbsByPlace.get(pass.toPlace) ?? [];
          climbs.push(pass);
          climbsByPlace.set(pass.toPlace, climbs);
        }
      }
      const placePasses: PodiumPass[] = [];
      for (const climbs of climbsByPlace.values()) {
        const summary = summarizePlace(climbs.sort(comparePodiumPasses), next);
        if (summary) {
          placePasses.push(summary);
        }
      }
      passes.push(...placePasses.sort(comparePodiumPasses));
    }

    previous = next;
  }

  return passes;
}

export function detectPodiumPassesForParties(source: {
  parties: readonly PodiumPartySource[];
  participants: readonly PodiumParticipantSource[];
  votes: readonly PodiumVoteSource[];
  categories: readonly PodiumCategorySource[];
  results: readonly PodiumResultSource[];
}): Record<string, PodiumPass[]> {
  const resultsByCategory = new Map<
    string,
    { nomineeIds: string[]; announcedAt: Date | string | null }
  >();
  for (const result of source.results) {
    const existing = resultsByCategory.get(result.categoryId);
    if (!existing) {
      resultsByCategory.set(result.categoryId, {
        nomineeIds: [result.nomineeId],
        announcedAt: result.announcedAt,
      });
      continue;
    }
    existing.nomineeIds.push(result.nomineeId);
    const nextAt = announcedAtIso(result.announcedAt);
    const currentAt = announcedAtIso(existing.announcedAt);
    if (nextAt && (!currentAt || nextAt < currentAt)) {
      existing.announcedAt = result.announcedAt;
    }
  }

  const categoriesByShow = new Map<string, TieBreakerCategoryInput[]>();
  for (const category of source.categories) {
    const winners = resultsByCategory.get(category.id);
    const list = categoriesByShow.get(category.awardShowId) ?? [];
    list.push({
      id: category.id,
      orderIndex: category.orderIndex,
      resultNomineeIds: winners?.nomineeIds ?? [],
      announcedAt: winners?.announcedAt ?? null,
    });
    categoriesByShow.set(category.awardShowId, list);
  }

  const participantsByParty = new Map<string, PodiumParticipant[]>();
  for (const participant of source.participants) {
    const list = participantsByParty.get(participant.partyId) ?? [];
    list.push({
      userId: participant.userId,
      name: getPartyMemberDisplayName(participant),
    });
    participantsByParty.set(participant.partyId, list);
  }

  const votesByParty = new Map<string, Record<string, Record<string, string>>>();
  for (const vote of source.votes) {
    const byUser = votesByParty.get(vote.partyId) ?? {};
    const userVotes = byUser[vote.userId] ?? {};
    userVotes[vote.categoryId] = vote.nomineeId;
    byUser[vote.userId] = userVotes;
    votesByParty.set(vote.partyId, byUser);
  }

  const passesByParty: Record<string, PodiumPass[]> = {};
  for (const party of source.parties) {
    passesByParty[party.id] = detectPodiumPasses({
      mode: parseTieBreakerMode(party.tieBreakerMode),
      participants: participantsByParty.get(party.id) ?? [],
      categories: categoriesByShow.get(party.awardShowId) ?? [],
      votesByUserId: votesByParty.get(party.id) ?? {},
    });
  }
  return passesByParty;
}
